import React, { useState, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "../../context/AuthContext";
import { getBuyerPackageHistories } from "../../services/packageServices.ts";
import { useToast } from "../../context/ToastContext";
import { useLanguage } from "../../context/LanguageContext";
import { formatPrice } from "../../utils/functions";
import api from "../../lib/api.ts";
import BuyerDeposit from "./BuyerDeposit";
import WithdrawOrders from "./WithdrawOrders";

const MyPlan = () => {
  const { user } = useAuth();
  const { showSuccess, showError } = useToast();
  const { language, translate } = useLanguage();
  const location = useLocation();
  const navigate = useNavigate();
  const isRTL = language === "ar";

  const [packageHistories, setPackageHistories] = useState([]);
  const [availablePackages, setAvailablePackages] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingPackages, setIsLoadingPackages] = useState(false);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState("my-packages");
  const [pagination, setPagination] = useState({
    currentPage: 1,
    totalPages: 1,
    totalItems: 0,
  });
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [selectedPackage, setSelectedPackage] = useState(null);
  const [receiptImage, setReceiptImage] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);

  // Calculate package counts based on status
  const getPackageCounts = () => {
    const activePackages = packageHistories.filter(
      (pkg) => pkg.isApproved == 1
    );
    const pendingPackages = packageHistories.filter(
      (pkg) => pkg.isApproved == 0 && pkg.actionBy == null
    );
    const rejectedPackages = packageHistories.filter(
      (pkg) => pkg.isApproved == 0 && pkg.actionBy != null
    );
    const totalHistory = packageHistories.length;

    return {
      active: activePackages.length,
      available: Array.isArray(availablePackages)
        ? availablePackages.length
        : 0,
      history: totalHistory,
      pending: pendingPackages.length,
      rejected: rejectedPackages.length,
      total: totalHistory,
    };
  };

  useEffect(() => {
    if (user && user.id) {
      fetchBuyerPackageHistories();
      fetchAvailablePackages();
    }
  }, [user]);

  // Sync activeTab with URL
  useEffect(() => {
    const path = location.pathname;
    if (path.includes("/profile/myplan/availablepackages")) {
      setActiveTab("available-packages");
    } else {
      setActiveTab("my-packages");
    }
  }, [location.pathname]);

  // Handle tab change and update URL
  const handleTabChange = (tab) => {
    setActiveTab(tab);
    if (tab === "available-packages") {
      navigate("/profile/myplan/availablepackages");
    } else {
      navigate("/profile/myplan");
    }
  };

  const fetchBuyerPackageHistories = async (page = 1) => {
    try {
      setIsLoading(true);
      setError(null);

      const response = await getBuyerPackageHistories(user.id, page, 50);

      if (response && response.data) {
        setPackageHistories(response.data);
        setPagination({
          currentPage: response.currentPage || page,
          totalPages: response.totalPages || 1,
          totalItems: response.totalItems || response.data.length,
        });
      } else if (Array.isArray(response)) {
        // Handle case where response is directly an array
        setPackageHistories(response);
        setPagination({
          currentPage: page,
          totalPages: 1,
          totalItems: response.length,
        });
      } else {
        setPackageHistories([]);
      }
    } catch (err) {
      console.error("Error fetching buyer package histories:", err);
      setError("Failed to load package information");
      showError("Failed to load package information");
    } finally {
      setIsLoading(false);
    }
  };

  // Fetch all available packages
  const fetchAvailablePackages = async () => {
    try {
      setIsLoadingPackages(true);
      setError(null);

      const response = await api.get("/buyerpackages/paginated", {
        params: {
          page: 1,
          limit: 50,
          orderBy: "id",
          order: "DESC",
          search: "",
          slug: "buyerPackages",
        },
      });

      // Handle different response structures
      let packages = [];
      if (response && response.data) {
        if (Array.isArray(response.data)) {
          packages = response.data;
        } else if (response.data.data && Array.isArray(response.data.data)) {
          packages = response.data.data;
        } else if (
          response.data.packages &&
          Array.isArray(response.data.packages)
        ) {
          packages = response.data.packages;
        } else if (
          response.data.results &&
          Array.isArray(response.data.results)
        ) {
          packages = response.data.results;
        }
      } else if (Array.isArray(response)) {
        packages = response;
      }

      setAvailablePackages(packages);
    } catch (err) {
      console.error("Error fetching available packages:", err);
      setError("Failed to load available packages");
      showError("Failed to load available packages");
    } finally {
      setIsLoadingPackages(false);
    }
  };

  // Show confirmation dialog
  const showConfirmationDialog = (pkg) => {
    setSelectedPackage(pkg);
    setReceiptImage(null);
    setImagePreview(null);
    setShowConfirmDialog(true);
  };

  // Handle image selection
  const handleImageChange = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      setReceiptImage(file);
      const reader = new FileReader();
      reader.onload = (e) => {
        setImagePreview(e.target?.result);
      };
      reader.readAsDataURL(file);
    }
  };

  // Close confirmation dialog
  const closeConfirmDialog = () => {
    setShowConfirmDialog(false);
    setSelectedPackage(null);
    setReceiptImage(null);
    setImagePreview(null);
  };

  // Confirm and request package
  const confirmAndRequestPackage = async () => {
    if (!selectedPackage) return;

    // Validate receipt image is provided
    if (!receiptImage) {
      setError("Please upload a receipt image to proceed with the request.");
      showError("Please upload a receipt image to proceed with the request.");
      return;
    }

    try {
      setIsLoading(true);
      setError(null);

      // Create FormData for file upload
      const formData = new FormData();
      formData.append("buyerPackageId", selectedPackage.id);
      formData.append("userId", user.id);
      formData.append("receiptImage", receiptImage);

      const response = await api.post("/buyerpackagehistories", formData, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      });

      if (response.data) {
        // Close dialog and refresh data
        setShowConfirmDialog(false);
        setSelectedPackage(null);
        setReceiptImage(null);
        setImagePreview(null);
        await fetchBuyerPackageHistories();
        showSuccess("Package request submitted successfully!");
      }
    } catch (err) {
      console.error("Error requesting package:", err);
      // move the page to the top
      window.scrollTo(0, 0);
      setError(err.response.data.message);
      showError(err.response.data.message);
      setShowConfirmDialog(false);
      setSelectedPackage(null);
      setReceiptImage(null);
      setImagePreview(null);
    } finally {
      setIsLoading(false);
    }
  };

  if (isLoading || isLoadingPackages) {
    return (
      <div className='flex justify-center items-center h-64'>
        <div className='animate-spin rounded-full h-12 w-12 border-b-2 border-[#F15A29]'></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className='bg-red-50 border border-red-200 rounded-lg p-4'>
        <div className='flex'>
          <div className='text-red-600'>
            <svg className='w-5 h-5' fill='currentColor' viewBox='0 0 20 20'>
              <path
                fillRule='evenodd'
                d='M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z'
                clipRule='evenodd'
              />
            </svg>
          </div>
          <div className='ml-3'>
            <h3 className='text-sm font-medium text-red-800'>Error</h3>
            <p className='text-sm text-red-700 mt-1'>{error}</p>
          </div>
        </div>
      </div>
    );
  }

  if (!user?.id) {
    return (
      <div className='bg-yellow-50 border border-yellow-200 rounded-lg p-4'>
        <div className='flex'>
          <div className='text-yellow-600'>
            <svg className='w-5 h-5' fill='currentColor' viewBox='0 0 20 20'>
              <path
                fillRule='evenodd'
                d='M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z'
                clipRule='evenodd'
              />
            </svg>
          </div>
          <div className='ml-3'>
            <h3 className='text-sm font-medium text-yellow-800'>
              No User Account
            </h3>
            <p className='text-sm text-yellow-700 mt-1'>
              You need to be logged in to view your packages.
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div
      className='bg-[#FFF9F9] rounded-[8px] sm:rounded-lg shadow-md'
      dir={isRTL ? "rtl" : "ltr"}
      lang={language}
    >
      {error && (
        <div className='bg-red-50 border border-red-200 rounded-lg p-4 mb-5'>
          <div className='flex'>
            <div className='text-red-600'>
              <svg className='w-5 h-5' fill='currentColor' viewBox='0 0 20 20'>
                <path
                  fillRule='evenodd'
                  d='M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z'
                  clipRule='evenodd'
                />
              </svg>
            </div>
            <div className='ml-3'>
              <h3 className='text-sm font-medium text-red-800'>Error</h3>
              <p className='text-sm text-red-700 mt-1'>{error}</p>
            </div>
          </div>
        </div>
      )}

      {/* Header */}
      <div className='bg-[#fcfcfc1f] text-gray-700 font-medium rounded-t-[8px] sm:rounded-t-[16px] px-1 py-1'>
        <div className='p-4 sm:p-[20px] text-base sm:text-lg rounded-t-[8px] sm:rounded-t-[12px] font-semibold text-gray-700 bg-[#F153291F] border-b border-black pb-2'>
          <div className='flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2'>
            <h2 className='text-sm sm:text-base md:text-lg font-semibold'>
              {translate("buyerPackages.heading")}
            </h2>
          </div>
        </div>
      </div>

      {/* Filter Navigation */}
      <div className='border-b border-gray-200'>
        {/* Filter Tabs */}
        <div className='flex flex-col lg:flex-row lg:items-center lg:justify-between gap-2 sm:gap-3 md:gap-4 py-2.5 sm:py-3 md:py-4'>
          {/* Filter Tabs */}
          <nav className='flex overflow-x-auto px-2 sm:px-3 md:px-5 pb-2 lg:pb-0 scrollbar-hide gap-1.5 sm:gap-2 md:gap-0'>
            {(() => {
              const counts = getPackageCounts();
              return [
                {
                  key: "my-packages",
                  label: translate("buyerPackages.tabs.activePackage"),
                  count: counts.active,
                },
                {
                  key: "available-packages",
                  label: translate("buyerPackages.tabs.availablePackages"),
                  count: counts.available,
                },
                {
                  key: "history",
                  label: translate("buyerPackages.tabs.packageHistory"),
                  count: counts.history,
                },
              ];
            })().map((filter) => {
              return (
                <button
                  key={filter.key}
                  onClick={() => setActiveTab(filter.key)}
                  className={`flex-shrink-0 px-2 sm:px-2.5 md:px-3 mx-0.5 sm:mx-1 py-1.5 sm:py-2 rounded-full font-medium text-[11px] sm:text-xs md:text-sm transition-all duration-200 flex items-center gap-1 sm:gap-1.5 md:gap-2
             ${
               activeTab === filter.key
                 ? "bg-gradient-to-r from-[#FF4A33] to-[#FFB74D] text-white shadow-md"
                 : "bg-gray-100 text-gray-600 hover:bg-gray-200 hover:text-gray-800"
             }`}
                >
                  <span className='whitespace-nowrap'>{filter.label}</span>
                  <span
                    className={`px-1 sm:px-1.5 md:px-2 py-0.5 rounded-full text-[9px] sm:text-[10px] md:text-xs font-semibold ${
                      activeTab === filter.key
                        ? "bg-white/20 text-white"
                        : "bg-gray-200 text-gray-600"
                    }`}
                  >
                    {filter.count}
                  </span>
                </button>
              );
            })}
          </nav>

          {/* Refund Deposit Button - aligned left to avoid right border merge */}
          {/* <div className='px-2 sm:px-3 md:px-5 lg:px-0 lg:mr-4 w-full lg:w-auto'>
            <button
              onClick={() => setActiveTab("withdraw")}
              className='w-full lg:w-auto px-2.5 sm:px-3 md:px-4 py-1.5 sm:py-2 bg-black text-white rounded-[8px] sm:rounded-lg hover:bg-gray-800 transition-colors text-[11px] sm:text-xs md:text-sm font-medium whitespace-nowrap'
            >
              Refund Deposit
            </button>
          </div> */}
        </div>
      </div>

      <div className='px-2 sm:px-3 md:px-4 lg:px-5 py-3 sm:py-4 md:py-5'>
        {activeTab === "my-packages" ? (
          // Active Package Tab
          packageHistories.filter((pkg) => pkg.isApproved == 1).length === 0 ? (
            <div className='text-center py-6 sm:py-8'>
              <div className='text-gray-400 mb-3 sm:mb-4'>
                <svg
                  className='mx-auto h-10 w-10 sm:h-12 sm:w-12'
                  fill='none'
                  viewBox='0 0 24 24'
                  stroke='currentColor'
                >
                  <path
                    strokeLinecap='round'
                    strokeLinejoin='round'
                    strokeWidth={2}
                    d='M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4'
                  />
                </svg>
              </div>
              <h3 className='text-base sm:text-lg font-medium text-gray-900 mb-2'>
                {translate("buyerPackages.messages.noPackages")}
              </h3>
              <p className='text-sm sm:text-base text-gray-500 px-4'>
                {translate("buyerPackages.messages.noPackagesYet")}
              </p>
            </div>
          ) : (
            <div className='grid grid-cols-1 lg:grid-cols-2 gap-3 sm:gap-4 md:gap-6'>
              {packageHistories
                .filter((pkg) => pkg.isApproved == 1)
                .map((packageHistory, index) => (
                  <div key={index} className='mb-3 sm:mb-4 md:mb-6'>
                    {/* Package History Card */}
                    <div className='border-2 bg-white border-[#F153291F] rounded-[8px] sm:rounded-lg shadow-lg h-full flex flex-col'>
                      {/* Card Header - Package Name and Status */}
                      <div className='bg-[#F153291F] text-white p-3 sm:p-3 md:p-4 rounded-t-[8px] sm:rounded-t-lg flex flex-col sm:flex-row sm:justify-between sm:items-center gap-2 sm:gap-2'>
                        <h3 className='text-base sm:text-base md:text-lg text-black font-semibold break-words'>
                          {packageHistory.name || "Package"}
                        </h3>
                        <span
                          className={`px-1.5 sm:px-2 md:px-3 py-0.5 sm:py-1 rounded-full text-[9px] sm:text-[10px] md:text-xs font-medium whitespace-nowrap ${
                            packageHistory.isApproved == 0 &&
                            packageHistory.actionBy == null
                              ? "bg-blue-500 text-white"
                              : packageHistory.isApproved == 0 &&
                                packageHistory.actionBy != null
                              ? "bg-red-500 text-white"
                              : "bg-green-500 text-white"
                          }`}
                        >
                          {packageHistory.isApproved == 0 &&
                          packageHistory.actionBy == null
                            ? translate("buyerPackages.status.pending")
                            : packageHistory.isApproved == 0 &&
                              packageHistory.actionBy != null
                            ? translate("buyerPackages.status.rejected")
                            : translate("buyerPackages.status.approved")}
                        </span>
                      </div>

                      {/* Card Body - Package Details */}
                      <div className='p-3 sm:p-2.5 md:p-3'>
                        {/* Package Details Grid */}
                        <div className='grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2 sm:gap-2'>
                          <div className='bg-gray-100 p-2.5 sm:p-2 md:p-3 rounded-[6px] sm:rounded-lg'>
                            <div className='text-xs sm:text-xs md:text-sm text-gray-400 mb-1 sm:mb-1'>
                              {translate("buyerPackages.labels.biddingLimit")}
                            </div>
                            <div className='text-sm sm:text-xs md:text-sm text-gray-800 font-medium break-words'>
                              {formatPrice(packageHistory.creditLimit) || "-"}
                            </div>
                          </div>

                          <div className='bg-gray-100 p-2.5 sm:p-2 md:p-3 rounded-[6px] sm:rounded-lg'>
                            <div className='text-xs sm:text-xs md:text-sm text-gray-400 mb-1 sm:mb-1'>
                              {translate("buyerPackages.labels.deposit")}
                            </div>
                            <div className='text-sm sm:text-xs md:text-sm text-gray-800 font-medium break-words'>
                              {formatPrice(packageHistory.deposit) || "-"}
                            </div>
                          </div>

                          <div className='bg-gray-100 p-2.5 sm:p-2 md:p-3 rounded-[6px] sm:rounded-lg'>
                            <div className='text-xs sm:text-xs md:text-sm text-gray-400 mb-1 sm:mb-1'>
                              {translate("buyerPackages.labels.maxCarsAllowed")}
                            </div>
                            <div className='text-sm sm:text-xs md:text-sm text-gray-800 font-medium'>
                              {packageHistory.carLimit || "-"}
                            </div>
                          </div>

                          <div className='bg-gray-100 p-2.5 sm:p-2 md:p-3 rounded-[6px] sm:rounded-lg'>
                            <div className='text-xs sm:text-xs md:text-sm text-gray-400 mb-1 sm:mb-1'>
                              {translate(
                                "buyerPackages.labels.freeParkingDays"
                              )}
                            </div>
                            <div className='text-sm sm:text-xs md:text-sm text-gray-800 font-medium'>
                              {packageHistory.freeParkingDays || "-"}{" "}
                              {translate("buyerPackages.labels.days")}
                            </div>
                          </div>

                          <div className='bg-gray-100 p-2.5 sm:p-2 md:p-3 rounded-[6px] sm:rounded-lg'>
                            <div className='text-xs sm:text-xs md:text-sm text-gray-400 mb-1 sm:mb-1'>
                              {translate(
                                "buyerPackages.labels.fineAfterFreeParking"
                              )}
                            </div>
                            <div className='text-sm sm:text-xs md:text-sm text-gray-800 font-medium break-words'>
                              {formatPrice(
                                packageHistory.fineAfterFreeParkingDays
                              ) || "-"}
                            </div>
                          </div>
                        </div>

                        {/* Rejection Reason */}
                        {packageHistory.isApproved == 0 &&
                          packageHistory.actionBy != null && (
                            <div className='w-full mt-2 sm:mt-3 md:mt-4 bg-red-100 text-red-800 text-[10px] sm:text-xs md:text-sm p-1.5 sm:p-2 md:p-3 rounded-[6px] sm:rounded-lg'>
                              <strong>
                                {translate(
                                  "buyerPackages.messages.rejectionReason"
                                )}
                              </strong>{" "}
                              {packageHistory.rejectionNote}
                            </div>
                          )}
                      </div>

                      {/* Card Footer - Assigned Date */}
                      <div className='bg-gray-50 p-3 sm:p-3 md:p-4 rounded-b-[8px] sm:rounded-b-lg border-t'>
                        <div className='flex flex-col sm:flex-row sm:justify-between sm:items-center gap-2.5 sm:gap-2'>
                          <span className='text-xs sm:text-xs md:text-sm text-gray-600 font-medium break-words'>
                            {translate("buyerPackages.labels.assignedDate")}:{" "}
                            {packageHistory.actionAt
                              ? (() => {
                                  const d = new Date(packageHistory.actionAt);
                                  const day = d.getDate();
                                  const month = d.getMonth() + 1;
                                  const year = d.getFullYear();
                                  return `${day}/${month}/${year}`;
                                })()
                              : translate("buyerPackages.labels.notAssigned")}
                          </span>
                          <button
                            onClick={() => setActiveTab("withdraw")}
                            className='w-full sm:w-auto px-3 sm:px-3 md:px-2 py-2 sm:py-1 rounded-[6px] sm:rounded transition-colors text-sm sm:text-xs md:text-sm whitespace-nowrap bg-black text-white hover:bg-gray-800 font-medium'
                          >
                            {translate("buyerPackages.buttons.refundDeposit")}
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
            </div>
          )
        ) : activeTab === "available-packages" ? (
          // Available Packages Tab
          !Array.isArray(availablePackages) ||
          availablePackages.length === 0 ? (
            <div className='text-center py-6 sm:py-8'>
              <div className='text-gray-400 mb-3 sm:mb-4'>
                <svg
                  className='mx-auto h-10 w-10 sm:h-12 sm:w-12'
                  fill='none'
                  viewBox='0 0 24 24'
                  stroke='currentColor'
                >
                  <path
                    strokeLinecap='round'
                    strokeLinejoin='round'
                    strokeWidth={2}
                    d='M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4'
                  />
                </svg>
              </div>
              <h3 className='text-base sm:text-lg font-medium text-gray-900 mb-2'>
                {translate("buyerPackages.messages.noAvailablePackages")}
              </h3>
              <p className='text-sm sm:text-base text-gray-500 px-4'>
                {translate("buyerPackages.messages.noAvailablePackagesMessage")}
              </p>
            </div>
          ) : (
            <div className='grid grid-cols-1 lg:grid-cols-2 gap-3 sm:gap-4 md:gap-6'>
              {Array.isArray(availablePackages) &&
                availablePackages
                  .sort((a, b) => (a.deposit || 0) - (b.deposit || 0))
                  .map((pkg, index) => (
                    <div key={index} className='mb-3 sm:mb-4 md:mb-3'>
                      {/* Available Package Card */}
                      <div className='border-2 bg-white border-[#F153291F] rounded-[8px] sm:rounded-lg shadow-lg h-full flex flex-col'>
                        {/* Card Header - Package Name and Status */}
                        <div className='bg-[#F153291F] text-white p-3 sm:p-3 md:p-4 rounded-t-[8px] sm:rounded-t-lg flex flex-col sm:flex-row sm:justify-between sm:items-center gap-2 sm:gap-2'>
                          <h3 className='text-base sm:text-base md:text-lg text-black font-semibold break-words'>
                            {pkg.name || "Package"}
                          </h3>
                          <span className='px-1.5 sm:px-2 md:px-3 py-0.5 sm:py-1 rounded-full text-[9px] sm:text-[10px] md:text-xs font-medium bg-[#F15329] text-white whitespace-nowrap'>
                            {translate("buyerPackages.status.available")}
                          </span>
                        </div>

                        {/* Card Body - Package Details */}
                        <div className='p-3 sm:p-2.5 md:p-3'>
                          {/* Package Details Grid */}
                          <div className='grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2 sm:gap-2'>
                            <div className='bg-gray-100 p-2.5 sm:p-2 md:p-3 rounded-[6px] sm:rounded-lg'>
                              <div className='text-xs sm:text-xs md:text-sm text-gray-400 mb-1 sm:mb-1'>
                                {translate("buyerPackages.labels.biddingLimit")}
                              </div>
                              <div className='text-sm sm:text-xs md:text-sm text-gray-800 font-medium break-words'>
                                {formatPrice(pkg.creditLimit) || "-"}
                              </div>
                            </div>

                            <div className='bg-gray-100 p-2.5 sm:p-2 md:p-3 rounded-[6px] sm:rounded-lg'>
                              <div className='text-xs sm:text-xs md:text-sm text-gray-400 mb-1 sm:mb-1'>
                                {translate("buyerPackages.labels.deposit")}
                              </div>
                              <div className='text-sm sm:text-xs md:text-sm text-gray-800 font-medium break-words'>
                                {formatPrice(pkg.deposit) || "-"}
                              </div>
                            </div>

                            <div className='bg-gray-100 p-2.5 sm:p-2 md:p-3 rounded-[6px] sm:rounded-lg'>
                              <div className='text-xs sm:text-xs md:text-sm text-gray-400 mb-1 sm:mb-1'>
                                {translate(
                                  "buyerPackages.labels.maxCarsAllowed"
                                )}
                              </div>
                              <div className='text-sm sm:text-xs md:text-sm text-gray-800 font-medium'>
                                {pkg.carLimit || "-"}
                              </div>
                            </div>

                            <div className='bg-gray-100 p-2.5 sm:p-2 md:p-3 rounded-[6px] sm:rounded-lg'>
                              <div className='text-xs sm:text-xs md:text-sm text-gray-400 mb-1 sm:mb-1'>
                                {translate(
                                  "buyerPackages.labels.freeParkingDays"
                                )}
                              </div>
                              <div className='text-sm sm:text-xs md:text-sm text-gray-800 font-medium'>
                                {pkg.freeParkingDays || "-"}{" "}
                                {translate("buyerPackages.labels.days")}
                              </div>
                            </div>

                            <div className='bg-gray-100 p-2.5 sm:p-2 md:p-3 rounded-[6px] sm:rounded-lg'>
                              <div className='text-xs sm:text-xs md:text-sm text-gray-400 mb-1 sm:mb-1'>
                                {translate(
                                  "buyerPackages.labels.fineAfterFreeParking"
                                )}
                              </div>
                              <div className='text-sm sm:text-xs md:text-sm text-gray-800 font-medium break-words'>
                                {formatPrice(pkg.fineAfterFreeParkingDays) ||
                                  "-"}
                              </div>
                            </div>
                          </div>

                          {/* Package Description */}
                          {pkg.description && (
                            <div className='mt-2 sm:mt-3 md:mt-4 bg-blue-50 p-1.5 sm:p-2 md:p-3 rounded-[6px] sm:rounded-lg'>
                              <h4 className='text-[10px] sm:text-xs md:text-sm font-medium text-gray-700 mb-1 sm:mb-2'>
                                {translate("sectionHeadings.description")}:
                              </h4>
                              <p className='text-[10px] sm:text-xs md:text-sm text-gray-600 break-words'>
                                {pkg.description}
                              </p>
                            </div>
                          )}
                        </div>

                        {/* Card Footer - Request Button */}
                        <div className='bg-gray-50 p-3 sm:p-3 md:p-4 rounded-b-[8px] sm:rounded-b-lg border-t'>
                          <button
                            onClick={() => showConfirmationDialog(pkg)}
                            disabled={isLoading}
                            className='w-full bg-[#F15A29] text-white py-2.5 sm:py-2 md:py-2.5 px-3 sm:px-4 rounded-[6px] sm:rounded-lg text-sm sm:text-xs md:text-sm font-medium hover:bg-[#E54A1A] disabled:opacity-50 disabled:cursor-not-allowed transition-colors'
                          >
                            {isLoading
                              ? translate("buyerPackages.buttons.requesting")
                              : translate(
                                  "buyerPackages.buttons.requestPackage"
                                )}
                          </button>
                        </div>
                      </div>
                    </div>
                  ))}
            </div>
          )
        ) : activeTab === "withdraw" ? (
          // Withdraw Tab
          <div className='bg-white rounded-lg shadow-sm'>
            <WithdrawOrders
              onClose={() => setActiveTab("my-packages")}
              activePackage={packageHistories.find(
                (pkg) => pkg.isApproved == 1
              )}
            />
          </div>
        ) : (
          // History Tab
          <div className='bg-gray-50 p-4 rounded-b-lg border-t'>
            <BuyerDeposit />
          </div>
        )}

        {/* Pagination for Active Package */}
        {activeTab === "my-packages" &&
          packageHistories.length > 0 &&
          pagination.totalPages > 1 && (
            <div className='flex flex-col sm:flex-row justify-center items-center mt-3 sm:mt-4 md:mt-6 gap-2 sm:gap-2.5 md:gap-2'>
              <button
                onClick={() =>
                  fetchBuyerPackageHistories(pagination.currentPage - 1)
                }
                disabled={pagination.currentPage === 1}
                className='w-full sm:w-auto px-2.5 sm:px-3 md:px-4 py-1.5 sm:py-2 text-[11px] sm:text-xs md:text-sm font-medium text-gray-500 bg-white border border-gray-300 rounded-[6px] sm:rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors'
              >
                Previous
              </button>

              <span className='px-2.5 sm:px-3 py-1.5 sm:py-2 text-[11px] sm:text-xs md:text-sm text-gray-700 whitespace-nowrap'>
                Page {pagination.currentPage} of {pagination.totalPages}
              </span>

              <button
                onClick={() =>
                  fetchBuyerPackageHistories(pagination.currentPage + 1)
                }
                disabled={pagination.currentPage === pagination.totalPages}
                className='w-full sm:w-auto px-2.5 sm:px-3 md:px-4 py-1.5 sm:py-2 text-[11px] sm:text-xs md:text-sm font-medium text-gray-500 bg-white border border-gray-300 rounded-[6px] sm:rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors'
              >
                Next
              </button>
            </div>
          )}
      </div>

      {/* Confirmation Dialog */}
      {showConfirmDialog && (
        <div className='fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-2 sm:p-3 md:p-4'>
          <div className='bg-white rounded-[8px] sm:rounded-lg shadow-xl w-full max-w-4xl mx-auto max-h-[98vh] sm:max-h-[95vh] md:max-h-[90vh] overflow-y-auto'>
            {/* Header */}
            <div className='flex justify-between items-center p-3 sm:p-4 md:p-6 border-b border-gray-200'>
              <h3 className='text-sm sm:text-base md:text-lg lg:text-xl font-bold text-gray-800 pr-2 break-words'>
                {translate("buyerPackages.modal.requestPackageConfirmation")}
              </h3>
              <button
                onClick={closeConfirmDialog}
                className='text-gray-400 hover:text-gray-600 transition-colors flex-shrink-0'
              >
                <svg
                  className='w-5 h-5 sm:w-6 sm:h-6'
                  fill='none'
                  stroke='currentColor'
                  viewBox='0 0 24 24'
                >
                  <path
                    strokeLinecap='round'
                    strokeLinejoin='round'
                    strokeWidth={2}
                    d='M6 18L18 6M6 6l12 12'
                  />
                </svg>
              </button>
            </div>

            {/* Main Content - Two Column Layout */}
            <div className='p-3 sm:p-4 md:p-6'>
              <div className='grid grid-cols-1 lg:grid-cols-2 gap-3 sm:gap-4 md:gap-6 lg:gap-8'>
                {/* Left Side - Important Notice */}
                <div className='space-y-3 sm:space-y-4 md:space-y-6'>
                  <div className='bg-red-50 border border-red-200 rounded-[8px] sm:rounded-lg p-3 sm:p-4 md:p-6'>
                    <div className='flex flex-col sm:flex-row sm:items-center mb-2 sm:mb-3 md:mb-4 gap-2'>
                      <div className='flex items-center space-x-2'>
                        <svg
                          className='w-4 h-4 sm:w-5 sm:h-5 md:w-6 md:h-6 text-red-600'
                          fill='currentColor'
                          viewBox='0 0 20 20'
                        >
                          <path
                            fillRule='evenodd'
                            d='M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z'
                            clipRule='evenodd'
                          />
                        </svg>
                      </div>
                      <h4 className='text-sm sm:text-base md:text-lg font-bold text-gray-800'>
                        {translate("buyerPackages.modal.notice")}
                      </h4>
                    </div>

                    <p className='text-[11px] sm:text-xs md:text-sm lg:text-base text-gray-700 mb-2 sm:mb-3 md:mb-4 font-medium'>
                      {translate("buyerPackages.modal.noticeMessage")}
                    </p>

                    <div className='space-y-1.5 sm:space-y-2 mb-2 sm:mb-3 md:mb-4'>
                      <div className='flex items-start'>
                        <span className='text-red-600 mr-1.5 sm:mr-2 flex-shrink-0 text-xs'>
                          •
                        </span>
                        <span className='text-[10px] sm:text-xs md:text-sm text-red-700'>
                          {translate(
                            "buyerPackages.modal.packageWillBeActivated"
                          )}
                        </span>
                      </div>

                      <div className='flex items-start'>
                        <span className='text-red-600 mr-1.5 sm:mr-2 flex-shrink-0 text-xs'>
                          •
                        </span>
                        <span className='text-[10px] sm:text-xs md:text-sm text-red-700'>
                          {translate(
                            "buyerPackages.modal.uploadPaymentReceipt"
                          )}
                        </span>
                      </div>
                      <div className='flex items-start'>
                        <span className='text-red-600 mr-1.5 sm:mr-2 flex-shrink-0 text-xs'>
                          •
                        </span>
                        <span className='text-[10px] sm:text-xs md:text-sm text-red-700'>
                          {translate("buyerPackages.modal.waitForApproval")}
                        </span>
                      </div>
                    </div>

                    <div className='bg-red-100 border border-red-300 rounded-[6px] sm:rounded-md p-1.5 sm:p-2 md:p-3'>
                      <p className='text-[10px] sm:text-xs md:text-sm text-red-800 font-medium'>
                        {translate("buyerPackages.modal.ensureDepositPayment")}
                      </p>
                    </div>
                  </div>
                </div>

                {/* Right Side - Package Details and Actions */}
                <div className='space-y-3 sm:space-y-4 md:space-y-6'>
                  {/* Package Details */}
                  <div>
                    <h4 className='text-sm sm:text-base md:text-lg font-semibold text-gray-800 mb-2 sm:mb-3 md:mb-4'>
                      {translate("buyerPackages.modal.packageDetails")}
                    </h4>
                    <div className='bg-gray-50 rounded-[8px] sm:rounded-lg p-2 sm:p-3 md:p-4 space-y-1.5 sm:space-y-2 md:space-y-3'>
                      <div>
                        <span className='text-[10px] sm:text-xs md:text-sm text-gray-600'>
                          {translate("buyerPackages.labels.packageName")}:
                        </span>
                        <p className='font-semibold text-[11px] sm:text-xs md:text-sm text-gray-800 break-words mt-0.5'>
                          {selectedPackage?.name}
                        </p>
                      </div>
                      <div className='grid grid-cols-1 sm:grid-cols-2 gap-2 sm:gap-3 md:gap-4'>
                        <div>
                          <span className='text-[10px] sm:text-xs md:text-sm text-gray-600 block'>
                            {translate("buyerPackages.labels.depositRequired")}:
                          </span>
                          <p className='font-semibold text-[11px] sm:text-xs md:text-sm text-gray-800 mt-0.5'>
                            {formatPrice(selectedPackage?.deposit) || "-"}
                          </p>
                        </div>
                        <div>
                          <span className='text-[10px] sm:text-xs md:text-sm text-gray-600 block'>
                            {translate("buyerPackages.labels.biddingLimit")}:
                          </span>
                          <p className='font-semibold text-[11px] sm:text-xs md:text-sm text-gray-800 mt-0.5'>
                            {formatPrice(selectedPackage?.creditLimit) || "-"}
                          </p>
                        </div>
                        <div>
                          <span className='text-[10px] sm:text-xs md:text-sm text-gray-600 block'>
                            {translate("buyerPackages.labels.freeParking")}:
                          </span>
                          <p className='font-semibold text-[11px] sm:text-xs md:text-sm text-gray-800 mt-0.5'>
                            {selectedPackage?.freeParkingDays || "-"}{" "}
                            {translate("buyerPackages.labels.days")}
                          </p>
                        </div>
                        <div>
                          <span className='text-[10px] sm:text-xs md:text-sm text-gray-600 block'>
                            {translate("buyerPackages.labels.carLimit")}:
                          </span>
                          <p className='font-semibold text-[11px] sm:text-xs md:text-sm text-gray-800 mt-0.5'>
                            {selectedPackage?.carLimit || "-"}{" "}
                            {translate("buyerPackages.labels.cars")}
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Receipt Upload */}
                  <div>
                    <label className='block text-[10px] sm:text-xs md:text-sm font-medium text-gray-700 mb-1.5 sm:mb-2'>
                      {translate("buyerPackages.modal.paymentReceiptImage")}{" "}
                      <span className='text-red-500'>
                        {translate("buyerPackages.modal.required")}
                      </span>
                    </label>
                    <input
                      type='file'
                      accept='image/*'
                      onChange={handleImageChange}
                      className='block w-full text-[10px] sm:text-xs md:text-sm text-gray-500 file:mr-2 sm:file:mr-4 file:py-1 sm:file:py-1.5 md:file:py-2 file:px-2 sm:file:px-3 md:file:px-4 file:rounded-full file:border-0 file:text-[10px] sm:file:text-xs md:file:text-sm file:font-semibold file:bg-[#F15A29] file:text-white hover:file:bg-[#E54A1A] file:cursor-pointer'
                      required
                    />
                    <p className='text-[9px] sm:text-[10px] md:text-xs text-gray-500 mt-1'>
                      {translate("buyerPackages.modal.uploadReceiptMessage")}
                    </p>

                    {/* Image Preview */}
                    {imagePreview && (
                      <div className='mt-2 sm:mt-3'>
                        <label className='block text-[10px] sm:text-xs md:text-sm font-medium text-gray-700 mb-1.5 sm:mb-2'>
                          {translate("buyerPackages.modal.imagePreview")}
                        </label>
                        <div className='text-center'>
                          <img
                            src={imagePreview}
                            alt='Receipt preview'
                            className='mx-auto max-h-20 sm:max-h-24 md:max-h-32 max-w-full rounded-[6px] sm:rounded-lg border border-gray-300'
                          />
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Confirmation Checkbox */}
                  <div className='flex items-start space-x-2 sm:space-x-2.5 md:space-x-3'>
                    <input
                      type='checkbox'
                      id='confirmRequest'
                      checked={receiptImage !== null}
                      onChange={() => {}} // Controlled by file upload
                      className='mt-0.5 sm:mt-1 h-3.5 w-3.5 sm:h-4 sm:w-4 text-[#F15A29] focus:ring-[#F15A29] border-gray-300 rounded flex-shrink-0'
                    />
                    <label
                      htmlFor='confirmRequest'
                      className='text-[10px] sm:text-xs md:text-sm text-gray-700'
                    >
                      {translate("buyerPackages.modal.confirmationCheckbox")}
                    </label>
                  </div>

                  {/* Action Buttons */}
                  <div className='flex flex-col sm:flex-row gap-2 sm:gap-2.5 md:gap-3 sm:space-x-3 pt-2 sm:pt-3 md:pt-4'>
                    <button
                      onClick={closeConfirmDialog}
                      disabled={isLoading}
                      className='flex-1 px-3 sm:px-4 md:px-6 py-1.5 sm:py-2 md:py-3 text-[11px] sm:text-xs md:text-sm text-gray-700 bg-gray-200 rounded-[8px] sm:rounded-lg hover:bg-gray-300 disabled:opacity-50 disabled:cursor-not-allowed transition-colors font-medium'
                    >
                      {translate("buyerPackages.modal.cancel")}
                    </button>
                    <button
                      onClick={confirmAndRequestPackage}
                      disabled={isLoading || !receiptImage}
                      className='flex-1 px-3 sm:px-4 md:px-6 py-1.5 sm:py-2 md:py-3 text-[11px] sm:text-xs md:text-sm bg-[#F15A29] text-white rounded-[8px] sm:rounded-lg hover:bg-[#E54A1A] disabled:opacity-50 disabled:cursor-not-allowed transition-colors font-medium'
                    >
                      {isLoading
                        ? translate("buyerPackages.buttons.requesting")
                        : translate("buyerPackages.modal.confirmRequest")}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MyPlan;
